import { useState, useEffect, useCallback, useRef } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

/**
 * Spec 085 (D3, BR-3): which portal a sign-in comes from, sent as `X-Garagat-Client` on login and
 * refresh. Identity answers 403 `WRONG_APP` (and revokes the fresh session) when a dealer account
 * signs in through `partner-portal` or a non-dealer account through `dealer-portal`. No header keeps
 * the old behaviour, so sessions stored before this change refresh as they always did.
 */
export type PortalClient = 'partner-portal' | 'dealer-portal';

export const CLIENT_HEADER = 'X-Garagat-Client';
const CLIENT_STORAGE_KEY = 'portal_client';
/** Spec 085: the dealer booking wizard draft (sessionStorage); cleared on sign-out with the tokens. */
export const DEALER_WIZARD_STORAGE_KEY = 'dealer_booking_wizard';

export const DEALER_ROLES = ['DEALER_OWNER', 'DEALER_STAFF'];
const GARAGE_ROLES = ['GARAGE_OWNER', 'GARAGE_MANAGER', 'GARAGE_SUPPORT'];

/** The login page of the other portal, for the WRONG_APP message. */
export type WrongAppTarget = 'dealer' | 'partner';

/**
 * 403 `WRONG_APP` from identity. `target` is the portal the account belongs to: a refusal on the
 * partner login means a dealer account (`dealer`), on the dealer login a non-dealer one (`partner`).
 */
export class WrongAppError extends Error {
  readonly code = 'WRONG_APP';
  readonly target: WrongAppTarget;

  constructor(message: string, target: WrongAppTarget) {
    super(message);
    this.name = 'WrongAppError';
    this.target = target;
  }
}

function clientHeaders(client: PortalClient | null): Record<string, string> {
  return client ? { [CLIENT_HEADER]: client } : {};
}

/** The client saved next to the tokens it belongs to (same storage pick as {@link getTokens}). */
function storedClient(): PortalClient | null {
  const v = (localStorage.getItem('access_token') ? localStorage : sessionStorage).getItem(CLIENT_STORAGE_KEY);
  return v === 'partner-portal' || v === 'dealer-portal' ? v : null;
}

async function loginError(response: Response, client: PortalClient, fallback: string): Promise<Error> {
  const errorData = await response.json().catch(() => ({}));
  if (response.status === 403 && (errorData.code === 'WRONG_APP' || errorData.error === 'WRONG_APP')) {
    return new WrongAppError(errorData.message || 'WRONG_APP', client === 'partner-portal' ? 'dealer' : 'partner');
  }
  return new Error(errorData.message || fallback);
}

interface AuthUser {
  id: string;
  email: string;
  name: string;
  roles: string[];
}

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  token: string | null;
  user: AuthUser | null;
}

function parseJwt(token: string): Record<string, unknown> | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

function extractUser(accessToken: string): AuthUser | null {
  const parsed = parseJwt(accessToken);
  if (!parsed) return null;
  const realmAccess = parsed.realm_access as { roles?: string[] } | undefined;
  return {
    id: (parsed.sub as string) || '',
    email: (parsed.email as string) || '',
    name: (parsed.name as string) || (parsed.preferred_username as string) || '',
    roles: realmAccess?.roles || [],
  };
}

function getStorage(): Storage {
  return localStorage.getItem('remember_me') === 'true' ? localStorage : sessionStorage;
}

function storeTokens(data: { access_token: string; refresh_token: string; expires_in: number }, rememberMe?: boolean) {
  if (rememberMe !== undefined) {
    localStorage.setItem('remember_me', String(rememberMe));
  }
  const storage = getStorage();
  storage.setItem('access_token', data.access_token);
  storage.setItem('refresh_token', data.refresh_token);
  storage.setItem('token_expires_at', String(Date.now() + data.expires_in * 1000));
}

/** Saved with the tokens (not always in localStorage), so a session-only tab is not re-labelled by another tab's sign-in. */
function storeClient(client: PortalClient) {
  getStorage().setItem(CLIENT_STORAGE_KEY, client);
}

function getTokens() {
  // Check both storages — localStorage first (remember me), then sessionStorage
  const lsToken = localStorage.getItem('access_token');
  const ssToken = sessionStorage.getItem('access_token');
  if (lsToken) {
    return {
      accessToken: lsToken,
      refreshToken: localStorage.getItem('refresh_token'),
      expiresAt: localStorage.getItem('token_expires_at'),
    };
  }
  if (ssToken) {
    return {
      accessToken: ssToken,
      refreshToken: sessionStorage.getItem('refresh_token'),
      expiresAt: sessionStorage.getItem('token_expires_at'),
    };
  }
  return { accessToken: null, refreshToken: null, expiresAt: null };
}

function clearAllTokens() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('token_expires_at');
  localStorage.removeItem('remember_me');
  localStorage.removeItem(CLIENT_STORAGE_KEY);
  localStorage.removeItem('garagat_onboarding_draft');
  localStorage.removeItem('garagat_onboarding_user');
  sessionStorage.removeItem('access_token');
  sessionStorage.removeItem('refresh_token');
  sessionStorage.removeItem('token_expires_at');
  sessionStorage.removeItem(CLIENT_STORAGE_KEY);
  // Spec 085: the dealer booking wizard draft holds a buyer's name and masked phone.
  sessionStorage.removeItem(DEALER_WIZARD_STORAGE_KEY);
}

export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    token: null,
    user: null,
  });
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTokens = useCallback(() => {
    clearAllTokens();
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    setAuthState({ isAuthenticated: false, isLoading: false, token: null, user: null });
  }, []);

  // The client is captured per session: a refresh never picks up a client another tab saved later.
  const scheduleRefresh = useCallback((expiresIn: number, refreshToken: string, client: PortalClient | null) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    const delay = Math.max((expiresIn - 60) * 1000, 10000);
    refreshTimerRef.current = setTimeout(async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...clientHeaders(client) },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
        if (!response.ok) throw new Error('Refresh failed');
        const data = await response.json();
        const user = extractUser(data.access_token);
        storeTokens(data);
        setAuthState({ isAuthenticated: true, isLoading: false, token: data.access_token, user });
        scheduleRefresh(data.expires_in, data.refresh_token, client);
      } catch {
        clearTokens();
      }
    }, delay);
  }, [clearTokens]);

  // On mount: check for existing tokens
  useEffect(() => {
    const { accessToken, refreshToken, expiresAt } = getTokens();
    const client = storedClient();

    if (!accessToken || !refreshToken) {
      setAuthState(prev => ({ ...prev, isLoading: false }));
      return;
    }

    const now = Date.now();
    const expiresAtMs = expiresAt ? parseInt(expiresAt, 10) : 0;

    if (expiresAtMs > now) {
      const user = extractUser(accessToken);
      setAuthState({ isAuthenticated: true, isLoading: false, token: accessToken, user });
      const remainingSeconds = Math.floor((expiresAtMs - now) / 1000);
      scheduleRefresh(remainingSeconds, refreshToken, client);
    } else {
      (async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...clientHeaders(client) },
            body: JSON.stringify({ refresh_token: refreshToken }),
          });
          if (!response.ok) throw new Error('Refresh failed');
          const data = await response.json();
          const user = extractUser(data.access_token);
          storeTokens(data);
          setAuthState({ isAuthenticated: true, isLoading: false, token: data.access_token, user });
          scheduleRefresh(data.expires_in, data.refresh_token, client);
        } catch {
          clearTokens();
        }
      })();
    }

    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [clearTokens, scheduleRefresh]);

  const login = useCallback(async (
    email: string, password: string, rememberMe: boolean = false, client: PortalClient = 'partner-portal',
  ) => {
    const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...clientHeaders(client) },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      throw await loginError(response, client, 'Invalid email or password');
    }

    const data = await response.json();
    const user = extractUser(data.access_token);
    storeTokens(data, rememberMe);
    storeClient(client);
    setAuthState({ isAuthenticated: true, isLoading: false, token: data.access_token, user });
    scheduleRefresh(data.expires_in, data.refresh_token, client);
  }, [scheduleRefresh]);

  const loginByPhone = useCallback(async (
    phone: string, password: string, rememberMe: boolean = false, client: PortalClient = 'partner-portal',
  ) => {
    const response = await fetch(`${API_BASE_URL}/api/auth/login/phone`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...clientHeaders(client) },
      body: JSON.stringify({ phone, password }),
    });

    if (!response.ok) {
      throw await loginError(response, client, 'Invalid phone number or password');
    }

    const data = await response.json();
    const user = extractUser(data.access_token);
    storeTokens(data, rememberMe);
    storeClient(client);
    setAuthState({ isAuthenticated: true, isLoading: false, token: data.access_token, user });
    scheduleRefresh(data.expires_in, data.refresh_token, client);
  }, [scheduleRefresh]);

  const loginByUsername = useCallback(async (
    username: string, password: string, rememberMe: boolean = false, client: PortalClient = 'partner-portal',
  ) => {
    const response = await fetch(`${API_BASE_URL}/api/auth/login/username`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...clientHeaders(client) },
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      throw await loginError(response, client, 'Invalid username or password');
    }

    const data = await response.json();
    const user = extractUser(data.access_token);
    storeTokens(data, rememberMe);
    storeClient(client);
    setAuthState({ isAuthenticated: true, isLoading: false, token: data.access_token, user });
    scheduleRefresh(data.expires_in, data.refresh_token, client);
  }, [scheduleRefresh]);

  const logout = useCallback(async () => {
    const { refreshToken } = getTokens();
    if (refreshToken) {
      try {
        await fetch(`${API_BASE_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...clientHeaders(storedClient()) },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
      } catch {
        // Logout on server failed — clear local state anyway
      }
    }
    clearTokens();
  }, [clearTokens]);

  const register = useCallback(async (data: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    password: string;
    role?: string;
    username?: string;
  }) => {
    const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Registration failed');
    }

    const result = await response.json();

    // Do NOT auto-login after registration — user must manually sign in
    return result;
  }, []);

  // Spec 085: dealer users (DEALER_OWNER / DEALER_STAFF) use the dealer portal inside this app.
  const isDealer = authState.user != null && authState.user.roles.some(r => DEALER_ROLES.includes(r));

  const isAccessDenied = authState.isAuthenticated && authState.user != null
    && !isDealer
    && !authState.user.roles.some(r => GARAGE_ROLES.includes(r));

  // A token that authenticates but lacks any partner role (e.g. an admin
  // account signed in here by mistake) must not linger in storage — otherwise
  // the mount effect auto-restores it on every visit and strands the user on
  // the Access Denied screen. Purge persisted tokens but keep the in-memory
  // state so the screen (with its explanation + logout button) still renders;
  // the next visit then lands on the login form.
  useEffect(() => {
    if (isAccessDenied) {
      clearAllTokens();
    }
  }, [isAccessDenied]);

  return { ...authState, isDealer, isAccessDenied, login, loginByPhone, loginByUsername, logout, register };
}
